import { makeAutoObservable, runInAction } from "mobx";

/**
 * Mock ServiceStore Configuration
 */
interface MockStoreOptions<T> {
  mockData: T[];
  idField: keyof T;
  simulateDelay?: number;
  generateId?: () => number | string;
  /** When set, entities are persisted to localStorage under this key */
  persistKey?: string;
}

/**
 * Generic query parameters
 */
interface QueryParams {
  query?: Record<string, unknown>;
  $limit?: number;
  $skip?: number;
  $sort?: Record<string, 1 | -1>;
}

/**
 * MockServiceStore - A mock implementation of ServiceStore for development
 *
 * This class provides a fully functional mock store that can be swapped
 * for real API-backed ServiceStores when the backend is ready.
 *
 * Features:
 * - Full CRUD operations (find, get, create, patch, remove)
 * - Query filtering with $limit, $skip, $sort
 * - Simulated network delay
 * - Automatic ID generation
 * - MobX observable state
 *
 * Usage:
 * ```typescript
 * const mockPurchaseOrdersStore = new MockServiceStore<PurchaseOrder>({
 *   mockData: mockPurchaseOrders,
 *   idField: 'purchaseOrderId',
 *   simulateDelay: 300,
 * });
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class MockServiceStore<T extends { [key: string]: any }> {
  entities: T[] = [];
  loading = false;
  error: string | null = null;
  total = 0;

  private options: MockStoreOptions<T>;
  private nextId: number = 1;

  constructor(options: MockStoreOptions<T>) {
    this.options = {
      simulateDelay: 300,
      generateId: () => this.nextId++,
      ...options,
    };

    // Load from localStorage if persistKey is set, otherwise use mock data
    const persisted = this.loadFromStorage();
    this.entities = persisted ?? [...options.mockData];
    this.total = this.entities.length;

    // Find the max ID to set next ID
    const maxId = this.entities.reduce((max, entity) => {
      const id = entity[this.options.idField];
      if (typeof id === "number" && id > max) {
        return id;
      }
      return max;
    }, 0);
    this.nextId = maxId + 1;

    makeAutoObservable(this, {}, { autoBind: true });
  }

  /**
   * Load entities from localStorage
   */
  private loadFromStorage(): T[] | null {
    if (!this.options.persistKey) return null;
    try {
      const raw = localStorage.getItem(this.options.persistKey);
      if (raw) {
        return JSON.parse(raw) as T[];
      }
    } catch {
      // Ignore parse errors, fall back to mock data
    }
    return null;
  }

  /**
   * Save current entities to localStorage
   */
  private saveToStorage(): void {
    if (!this.options.persistKey) return;
    try {
      localStorage.setItem(this.options.persistKey, JSON.stringify(this.entities));
    } catch {
      // Ignore storage quota errors
    }
  }

  /**
   * Simulates network delay
   */
  private delay(): Promise<void> {
    return new Promise((resolve) =>
      setTimeout(resolve, this.options.simulateDelay || 300)
    );
  }

  /**
   * Apply query filters to entities
   */
  private applyQuery(
    entities: T[],
    params?: QueryParams
  ): { data: T[]; total: number } {
    let result = [...entities];
    const query = params?.query || {};

    // Apply filters
    Object.entries(query).forEach(([key, value]) => {
      if (key.startsWith("$")) return; // Skip special operators

      if (value !== undefined && value !== null) {
        if (typeof value === "object" && value !== null) {
          // Handle range queries like { $gte: '2024-01-01' }
          const rangeQuery = value as Record<string, unknown>;
          if ("$gte" in rangeQuery) {
            result = result.filter((e) => String(e[key]) >= String(rangeQuery.$gte));
          }
          if ("$lte" in rangeQuery) {
            result = result.filter((e) => String(e[key]) <= String(rangeQuery.$lte));
          }
          if ("$in" in rangeQuery && Array.isArray(rangeQuery.$in)) {
            result = result.filter((e) =>
              (rangeQuery.$in as unknown[]).includes(e[key])
            );
          }
        } else if (Array.isArray(value)) {
          // Handle array values (IN query)
          result = result.filter((e) => value.includes(e[key]));
        } else {
          // Exact match
          result = result.filter((e) => e[key] === value);
        }
      }
    });

    const total = result.length;

    // Apply sorting
    if (params?.$sort) {
      const sortEntries = Object.entries(params.$sort);
      result.sort((a, b) => {
        for (const [key, direction] of sortEntries) {
          const aVal = a[key];
          const bVal = b[key];
          if (aVal === bVal) continue;
          if (aVal === undefined || aVal === null) return 1;
          if (bVal === undefined || bVal === null) return -1;
          const comparison = String(aVal).localeCompare(String(bVal));
          return direction === 1 ? comparison : -comparison;
        }
        return 0;
      });
    }

    // Apply pagination
    const skip = params?.$skip || params?.query?.$skip || 0;
    const limit = params?.$limit || params?.query?.$limit || result.length;
    result = result.slice(Number(skip), Number(skip) + Number(limit));

    return { data: result, total };
  }

  /**
   * Find entities matching query
   */
  async find(params?: QueryParams): Promise<T[]> {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    await this.delay();

    const { data, total } = this.applyQuery(this.entities, params);

    runInAction(() => {
      this.total = total;
      this.loading = false;
    });

    return data;
  }

  /**
   * Get a single entity by ID
   */
  async get(id: string | number): Promise<T | undefined> {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    await this.delay();

    const entity = this.entities.find(
      (e) => e[this.options.idField] === id || e[this.options.idField] === Number(id)
    );

    runInAction(() => {
      this.loading = false;
    });

    return entity;
  }

  /**
   * Create a new entity
   */
  async create(data: Partial<T>): Promise<T> {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    await this.delay();

    const now = new Date().toISOString();
    const newEntity = {
      ...data,
      [this.options.idField]: this.options.generateId!(),
      createdAt: now,
      updatedAt: now,
    } as unknown as T;

    runInAction(() => {
      this.entities.push(newEntity);
      this.total = this.entities.length;
      this.loading = false;
    });
    this.saveToStorage();

    return newEntity;
  }

  /**
   * Update an existing entity
   */
  async patch(id: string | number, data: Partial<T>): Promise<T | undefined> {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    await this.delay();

    const index = this.entities.findIndex(
      (e) => e[this.options.idField] === id || e[this.options.idField] === Number(id)
    );

    if (index === -1) {
      runInAction(() => {
        this.loading = false;
        this.error = `Entity with ID ${id} not found`;
      });
      return undefined;
    }

    const updatedEntity = {
      ...this.entities[index],
      ...data,
      updatedAt: new Date().toISOString(),
    } as T;

    runInAction(() => {
      this.entities[index] = updatedEntity;
      this.loading = false;
    });
    this.saveToStorage();

    return updatedEntity;
  }

  /**
   * Remove an entity by ID
   */
  async remove(id: string | number): Promise<T | undefined> {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    await this.delay();

    const index = this.entities.findIndex(
      (e) => e[this.options.idField] === id || e[this.options.idField] === Number(id)
    );

    if (index === -1) {
      runInAction(() => {
        this.loading = false;
        this.error = `Entity with ID ${id} not found`;
      });
      return undefined;
    }

    const removed = this.entities[index];

    runInAction(() => {
      this.entities.splice(index, 1);
      this.total = this.entities.length;
      this.loading = false;
    });
    this.saveToStorage();

    return removed;
  }

  /**
   * Reset the store to initial mock data and clear persisted data
   */
  reset(): void {
    runInAction(() => {
      this.entities = [...this.options.mockData];
      this.total = this.entities.length;
      this.loading = false;
      this.error = null;
    });
    if (this.options.persistKey) {
      localStorage.removeItem(this.options.persistKey);
    }
  }

  /**
   * Clear all entities and persisted data
   */
  clear(): void {
    runInAction(() => {
      this.entities = [];
      this.total = 0;
      this.loading = false;
      this.error = null;
    });
    if (this.options.persistKey) {
      localStorage.removeItem(this.options.persistKey);
    }
  }
}

export default MockServiceStore;
